"use client"

import { useState } from "react"

import { ComponentShowcase } from "./component-showcase"
import type { ComponentCategory, ComponentRegistryEntry } from "./registry"

export interface CategorySectionProps {
  category: ComponentCategory
  groups: Record<string, ComponentRegistryEntry[]>
}

/**
 * GroupSection
 *
 * Displays a collapsible group of components (e.g., "cta", "features")
 */
function GroupSection({
  groupName,
  components,
  category,
}: {
  groupName: string
  components: ComponentRegistryEntry[]
  category: ComponentCategory
}) {
  const [isOpen, setIsOpen] = useState(category === "derived")

  return (
    <div className="border-border border-b last:border-b-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="hover:bg-muted/50 flex w-full items-center justify-between px-4 py-3 text-left transition-colors"
      >
        <div className="flex items-center gap-3">
          <svg
            className={`text-muted-foreground size-4 transition-transform ${
              isOpen ? "rotate-90" : ""
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
          <h3 className="text-foreground text-sm font-medium">
            {groupName}
            <span className="text-muted-foreground ml-2 text-xs">
              ({components.length} component{components.length !== 1 ? "s" : ""}
              )
            </span>
          </h3>
        </div>
      </button>

      {isOpen && (
        <div className="bg-muted/20 space-y-10 px-6 py-8">
          {components.map((entry) => (
            <ComponentShowcase
              key={entry.id}
              id={entry.id}
              name={entry.name}
              component={entry.component}
              description={entry.description}
              demoProps={entry.demoProps}
            />
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * CategorySection
 *
 * Displays a category (e.g., "Application", "Marketing") with collapsible groups
 */
export function CategorySection({ category, groups }: CategorySectionProps) {
  const [isOpen, setIsOpen] = useState(true)

  const totalComponents = Object.values(groups).reduce(
    (sum, components) => sum + components.length,
    0,
  )

  const categoryDisplayNames: Record<ComponentCategory, string> = {
    application: "Application",
    marketing: "Marketing",
    derived: "Derived (Custom)",
  }

  return (
    <div className="bg-card overflow-hidden rounded-lg border shadow-sm">
      {/* Category header */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="bg-muted/30 hover:bg-muted/50 flex w-full items-center justify-between px-6 py-4 text-left transition-colors"
      >
        <div>
          <h2 className="text-foreground text-lg font-semibold">
            {categoryDisplayNames[category]}
          </h2>
          <p className="text-muted-foreground mt-1 text-sm">
            {Object.keys(groups).length} group
            {Object.keys(groups).length !== 1 ? "s" : ""} · {totalComponents}{" "}
            component{totalComponents !== 1 ? "s" : ""}
          </p>
        </div>
        <svg
          className={`text-muted-foreground size-5 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Groups */}
      {isOpen && (
        <div>
          {Object.entries(groups)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([groupName, components]) => (
              <GroupSection
                key={`${category}-${groupName}`}
                groupName={groupName}
                components={components}
                category={category}
              />
            ))}
        </div>
      )}
    </div>
  )
}
